import { HttpClient, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { ValidateCredentials } from './models/validate-credentials.model';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { SignUp } from './models/sign-up.model';
import { Router } from '@angular/router';
import { NewPassword } from './models/new-password.model';
import { environmentProd } from 'src/environments/environment.prod';
import { environment as environmentUat } from 'src/environments/environment.uat';
import { DemoSignUpPayload, DemoSignUp } from './models/demo-sign-up.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  httpClient = inject(HttpClient);

  private authToken = 'authToken';

  constructor(
    private cookieService: CookieService,
    private router: Router
  ) {}

  logout(): void {
    this.deleteCookie();
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return !!this.getCookie();
  }

  onLoginSso(encodedJwt: string, email: string): Observable<string> {
    return this.httpClient.post<string>(
      `/api/Auth/LoginSso`,
      null,
      {
        params: {
          encodedJwt,
          email,
        },
      }
    );
  }

  onValidateEmail(email: string): Observable<ValidateCredentials> {
    return this.httpClient.post<ValidateCredentials>(
      `/api/Auth/ValidateEmail`,
      null,
      {
        params: {
          email,
        },
      }
    );
  }

  onLogin(email: string, password: string): Observable<string> {
    return this.httpClient.post<string>(
      `/api/Auth/Login`,
      null,
      {
        params: {
          email,
          password
        },
      }
    );
  }

  onLoginWithToken(token: string): Observable<ValidateCredentials> {
    return this.httpClient.post<ValidateCredentials>(
      `/api/Auth/ActivityTokenLogin`,
      null,
      {
        params: {
          token
        }
      }
    );
  }

  onRequestResetPassword(email: string): Observable<HttpStatusCode> {
    return this.httpClient.post<HttpStatusCode>(
      `/api/Auth/ForgotPassword`,
      null,
      {
        params: {
          email
        }
      }
    );
  }

  onValidateResetPasswordToken(token: string): Observable<HttpStatusCode> {
      return this.httpClient.post<HttpStatusCode>(
        `/api/Auth/ValidateForgotPasswordToken`,
        null,
        {
          params: {
            token
          }
        }
    );
  }

  onChangePassword(token: string, model: NewPassword): Observable<HttpStatusCode> {
    return this.httpClient.post<HttpStatusCode>(
        `/api/Auth/ChangePassword`,
        model,
        {
          params: {
            token
          }
        }
    );
  }

  async getSignUpData(token: string, language: string): Promise<SignUp> {
    return await firstValueFrom(this.httpClient.get<SignUp>(
      `/api/Auth/SignUp`,
      {
        params: {
          token,
          language
        }
      }
    ));
  }

  async onSignUp(model: SignUp): Promise<HttpStatusCode> {
    return await firstValueFrom(this.httpClient.post<HttpStatusCode>(
      `/api/Auth/SignUp`,
      model
    ));
  }

  async onConfirmSignUp(model: SignUp, language: string): Promise<HttpResponse<SignUp>> {
    return await firstValueFrom(
      this.httpClient.post<SignUp>(`/api/Auth/ConfirmSignUp`, model, {
        params: { language },
        observe: 'response'
      })
    );
  }


  async getDemoSignUpData(language?: string | null, partner?: string | null): Promise<DemoSignUp> {
    let params = new HttpParams();

    if (language) {
      params = params.set('language', language);
    }

    if (partner) {
      params = params.set('partner', partner);
    }

    return await firstValueFrom(
      this.httpClient.get<DemoSignUp>(
        '/api/Auth/DemoSignUp',
        { params }
      )
    );
  }

  async postDemoSignUpData(model: DemoSignUpPayload, language?: string | null): Promise<HttpStatusCode> {
    let params = new HttpParams();

    if (language) {
      params = params.set('language', language);
    }

    return await firstValueFrom(
      this.httpClient.post<HttpStatusCode>(
        '/api/Auth/DemoSignUp',
        model,
        { params }
      )
    );
  }

  async getCompanySignUpData(token: string, language?: string | null): Promise<DemoSignUp> {
    let params = new HttpParams().set('token', token);

    if (language) {
      params = params.set('language', language);
    }

    return await firstValueFrom(
      this.httpClient.get<DemoSignUp>(
        '/api/Auth/CompanySignUp',
        { params }
      )
    );
  }

  async postCompanySignUpData(
    model: DemoSignUpPayload,
    token: string,
    language?: string | null
  ): Promise<HttpStatusCode> {
    let params = new HttpParams().set('token', token);

    if (language) {
      params = params.set('language', language);
    }

    return await firstValueFrom(
      this.httpClient.post<HttpStatusCode>(
        '/api/Auth/CompanySignUp',
        model,
        { params }
      )
    );
  }

  setCookie(value: string, rememberMe = false): void {
    const host = window.location.hostname;
    const isUatOrProduction = host === environmentProd.host || host === environmentUat.host;

    const cookieOptions: CookieOptions = {
      expires: rememberMe ? 365 : 1,
      path: '/',
      domain: '',
      secure: isUatOrProduction,
      sameSite: 'Strict',
    };

    this.cookieService.set(this.authToken, value, cookieOptions);
  }

  getToken(): string | null {
    return this.getCookie();
  }

  private getCookie(): string | null {
    return this.cookieService.get(this.authToken);
  }

  private deleteCookie(): void {
    this.cookieService.delete(this.authToken);
  }
}
